import { whyChooseFeatures } from '@/mocks/homeData';
import { useInView } from '@/hooks/useInView';

function WhyCard({ feature, delay }: { feature: typeof whyChooseFeatures[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`group p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300/40 hover:bg-background-50 hover:shadow-md transition-all duration-500 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100/80 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
        <i className={`${feature.icon} text-xl`} />
      </div>
      <h3 className="text-base md:text-lg font-semibold text-foreground-900 mb-2">
        {feature.title}
      </h3>
      <p className="text-sm text-foreground-600 leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
}

export default function WhyChooseSection() {
  const [titleRef, titleInView] = useInView<HTMLDivElement>();

  return (
    <section id="trust" className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-background-100">
      {/* Header */}
      <div
        ref={titleRef}
        className={`text-center max-w-2xl mx-auto mb-10 md:mb-14 transition-all duration-700 ${
          titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
          <span className="text-xs font-medium text-accent-700 uppercase tracking-wider">Advantages</span>
        </div>
        <h2
          className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-3"
          style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
        >
          Why organizations choose Ardeun
        </h2>
        <p className="text-base md:text-lg text-foreground-600">
          Trusted by enterprises and governments for mission-critical biometric security.
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {whyChooseFeatures.map((feature, i) => (
          <WhyCard key={feature.id} feature={feature} delay={i * 80} />
        ))}
      </div>
    </section>
  );
}